import React, { useState, useEffect } from "react";
import {
  FaMapMarkedAlt,
  FaUsers,
  FaCogs,
  FaStar,
  FaAward,
  FaCheckCircle,
  FaQuoteLeft,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { motion } from "framer-motion";

// Data for the feature cards
const featuresData = [
  {
    icon: FaMapMarkedAlt,
    title: "Obszar działania",
    description:
      "Od marca 2012r. działamy na terenie Kielc i województwa świętokrzyskiego. Organizujemy lunche, bankiety, konferencje, szkolenia i uroczystości rodzinne.",
  },
  {
    icon: FaUsers,
    title: "Obsługa",
    description:
      "Zapewniamy pełen serwis: od przygotowania posiłków i dostarczenia ich na miejsce, po odpowiedni sprzęt i profesjonalną obsługę kelnerską.",
  },
  {
    icon: FaCogs,
    title: "Wyposażenie",
    description:
      "Oferujemy stoły, krzesła, termosy, termoporty, bemary, warniki, podgrzewacze i grille. Wszystkie elementy są praktyczne i idealne do obsługi różnego rodzaju eventów.",
  },
  {
    icon: FaStar,
    title: "Doświadczenie",
    description:
      "Mamy duże doświadczenie w przygotowywaniu posiłków regeneracyjnych oraz żywieniu dzieci w placówkach przedszkolnych i szkolnych.",
  },
  {
    icon: FaAward,
    title: "Profesjonalizm",
    description:
      "Dysponujemy profesjonalnie wyposażonym zapleczem kuchennym oraz kompetentnym i zgranym zespołem współpracowników.",
  },
  {
    icon: FaCheckCircle,
    title: "Kontrola",
    description:
      "Posiadamy wszystkie niezbędne odbiory sanepidu. Posiłki przygotowujemy zgodnie z rygorystycznymi zasadami systemu HACCP.",
  },
];

// Data for the testimonial cards
const testimonialsData = [
  {
    quote:
      "Polecam każdemu przepyszne jedzenie, super atmosfera, przemiła obsługa. Niczego nam nie brakowało. Polecam",
    author: "Marzena",
    rating: 5,
  },
  {
    quote:
      "Dziękuję za pyszne jedzenie dowożone na 4 dniowe spotkanie ok 100 osób. Wegańskie i wegetariańskie, świeże, dobrze przyprawione, smaczne.",
    author: "Monika",
    rating: 5,
  },
  {
    quote:
      "Zdecydowanie Wybieramy Anatomię Smaku. Zawsze pyszne i świeże jedzonko w bardzo przystępnej cenie.",
    author: "Piotr",
    rating: 5,
  },
  {
    quote:
      "Wszystko, super jedzenie smaczne świeże i na czas. A podejście do klienta poziom master, byłam niezwykle mile zaskoczona.",
    author: "Aleksandra",
    rating: 5,
  },
  {
    quote:
      "Obsługiwali kilka imprez na których byłem, skorzystałem też prywatnie (komunia). Bardzo polecam, jakość i smak warty swojej ceny!",
    author: "Igor",
    rating: 5,
  },
  {
    quote:
      "Obsługa i organizacja przyjęć na najwyższym poziomie, pełen profesjonalizm.",
    author: "Małgorzata",
    rating: 5,
  },
];

const FeatureCard = ({ icon: Icon, title, description, index }) => (
  <div
    className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 ease-out transform hover:-translate-y-2 border border-cream-100"
    style={{ animationDelay: `${index * 100}ms` }}
  >
    <div className="relative z-10">
      <div className="flex items-center justify-center bg-gradient-to-br from-cream-200 to-cream-300 group-hover:from-cream-600 group-hover:to-cream-700 rounded-2xl w-20 h-20 transition-all duration-500 transform group-hover:scale-110 group-hover:rotate-3">
        <Icon className="text-3xl text-cream-700 group-hover:text-white transition-colors duration-500" />
      </div>
      <h3 className="text-xl font-bold text-charcoal-800 mt-6 group-hover:text-cream-700 transition-colors duration-300">
        {title}
      </h3>
      <p className="text-charcoal-600 mt-3 leading-relaxed">{description}</p>
    </div>
  </div>
);

const StarRating = ({ rating }) => (
  <div className="flex gap-1">
    {[...Array(rating)].map((_, i) => (
      <FaStar key={i} className="text-yellow-400 text-sm" />
    ))}
  </div>
);

const TestimonialCard = ({ quote, author, rating }) => (
  <div className="bg-white rounded-3xl p-8 shadow-sm border border-cream-200 h-full flex flex-col relative mx-2">
    <FaQuoteLeft className="text-cream-200 text-4xl mb-4 opacity-40" />
    <div className="mb-4">
      <StarRating rating={rating} />
    </div>
    <p className="text-charcoal-700 italic text-sm leading-relaxed mb-6 flex-grow">
      "{quote}"
    </p>
    <footer className="pt-4 border-t border-cream-100">
      <p className="text-sm font-bold text-cream-700 uppercase tracking-widest">
        {author}
      </p>
    </footer>
  </div>
);

export default function Aboutus() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(3);

  // Obsługa responsywności slidera
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setCardsToShow(1);
      else if (window.innerWidth < 1024) setCardsToShow(2);
      else setCardsToShow(3);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev + 1 >= testimonialsData.length - (cardsToShow - 1) ? 0 : prev + 1,
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? testimonialsData.length - cardsToShow : prev - 1,
    );
  };

  return (
    <section className="relative overflow-hidden bg-white" id="aboutus">
      {/* Decorative shapes */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-cream-200 rounded-full blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cream-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 py-20">
        <div className="container max-w-7xl mx-auto px-4">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-base font-semibold text-cream-700 tracking-wider uppercase">
              Anatomia Smaku
            </h2>
            <p class="mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold text-charcoal-800 animate-fadeInUp animation-delay-100">
              Poznaj nas
              <span className="text-cream-700"> bliżej</span>
            </p>
            <p class="mt-6 text-xl text-charcoal-600 leading-relaxed animate-fadeInUp animation-delay-200">
              To nie tylko catering - to{" "}
              <span class="text-cream-700 font-semibold">LUDZIE z pasją</span>,
              którzy przygotują Twoje wymarzone wesele, komunię, czy spotkanie
              biznesowe w wybranym przez Ciebie miejscu.
            </p>
          </div>

          {/* Features Grid */}
          <div className="mt-20 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {featuresData.map((feature, index) => (
              <FeatureCard key={index} {...feature} index={index} />
            ))}
          </div>
        </div>

        {/* Testimonials Section with Slider */}
        <div className="mt-32 relative">
          {/* Skrzywione tło (Skew) */}
          <div className="absolute top-0 left-0 right-0 h-full bg-cream-100 transform -skew-y-3 z-0" />

          <div className="relative z-10 pt-24 pb-28 px-4 max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <p class="mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold text-charcoal-800 animate-fadeInUp animation-delay-100">
                Setki zadowolonych
                <br />
                <span className="text-cream-700">klientów</span>
              </p>

              {/* Trust badge */}
              <div className="mt-8 inline-flex items-center gap-4 bg-white px-6 py-3 rounded-full shadow-sm border border-cream-200">
                <div className="flex -space-x-3">
                  {/* Avatar 1 - Inicjał z kolorem charcoal */}
                  <div className="w-9 h-9 bg-charcoal-800 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                    M
                  </div>
                  {/* Avatar 2 - Ikona użytkownika z kolorem cream */}
                  <div className="w-9 h-9 bg-cream-600 rounded-full border-2 border-white flex items-center justify-center text-white shadow-sm">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  {/* Avatar 3 - Inicjał z innym odcieniem */}
                  <div className="w-9 h-9 bg-cream-700 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                    P
                  </div>
                  {/* Avatar 4 - Ikona uśmiechu (zadowolony klient) */}
                  <div className="w-9 h-9 bg-red-700 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                    A
                  </div>
                </div>
                <span className="text-[10px] font-black text-charcoal-700 uppercase tracking-widest">
                  + 50 pozytywnych opinii
                </span>
              </div>
            </div>

            {/* Slider Container */}
            <div className="relative overflow-hidden px-2">
              <motion.div
                className="flex"
                animate={{ x: `-${currentIndex * (100 / cardsToShow)}%` }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {testimonialsData.map((testimonial, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 px-2 transition-opacity duration-300"
                    style={{ width: `${100 / cardsToShow}%` }}
                  >
                    <TestimonialCard {...testimonial} />
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Slider Navigation */}
            <div className="mt-12 flex flex-col items-center gap-6">
              <div className="flex gap-4">
                <button
                  onClick={prevSlide}
                  className="w-12 h-12 rounded-full border border-cream-300 flex items-center justify-center text-charcoal-600 hover:bg-cream-700 hover:text-white transition-all shadow-sm bg-white"
                >
                  <FaChevronLeft />
                </button>
                <button
                  onClick={nextSlide}
                  className="w-12 h-12 rounded-full border border-cream-300 flex items-center justify-center text-charcoal-600 hover:bg-cream-700 hover:text-white transition-all shadow-sm bg-white"
                >
                  <FaChevronRight />
                </button>
              </div>

              {/* Dots / Indicators */}
              <div className="flex gap-2">
                {[...Array(testimonialsData.length - (cardsToShow - 1))].map(
                  (_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentIndex(i)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        currentIndex === i
                          ? "w-8 bg-cream-700"
                          : "w-2 bg-cream-300"
                      }`}
                    />
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
